import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  ShieldCheck,
  Zap,
  Star,
  Check,
  X,
  Lock,
  Cpu,
  Server,
  Terminal,
  ArrowUpRight,
  ChevronDown,
  AlertTriangle,
  Database,
  Layers,
  Wifi,
} from "lucide-react";
import { servicesContent } from "@/data/servicesContent";

const tierSlugs = ["ai-sandbox", "ai-onprem", "ai-appliance"];

// Air-gapped comparison rows
const comparison = [
  {
    trap: "Uncontrolled data leakage into public model training",
    te: "100% air-gapped · your data never leaves your walls",
  },
  { trap: "Exploding per-token API costs at scale", te: "$0 recurring token fees after deployment" },
  { trap: "Vendor lock-in to OpenAI / Anthropic pricing whims", te: "Owned model weights · portable across hardware" },
  { trap: "Junior hourly rates + 3-week onboarding gauntlets", te: "4-hour onboarding · founder-led architecture" },
  { trap: "Ambiguous compliance surface (GDPR / HIPAA / SOC 2)", te: "Deployment-time compliance mapping · audit-ready" },
  { trap: "Every prompt is a potential regulatory event", te: "Full observability · zero third-party training" },
];

// Local stack tags shown as inline chips
const stackTags = [
  { label: "Llama 3.3", accent: "violet" },
  { label: "Mistral", accent: "violet" },
  { label: "Qwen 3", accent: "violet" },
  { label: "Qdrant", accent: "emerald" },
  { label: "Open-WebUI", accent: "emerald" },
  { label: "LangChain", accent: "emerald" },
  { label: "LTX-2", accent: "purple" },
  { label: "Wan 2.1", accent: "purple" },
  { label: "vLLM", accent: "violet" },
];

const trustBadges = [
  { label: "100% Offline Capable", Icon: Wifi },
  { label: "Zero 3rd-Party Training", Icon: Lock },
  { label: "HIPAA / SOC 2 Ready", Icon: ShieldCheck },
];

const faqs = [
  {
    q: "What does 'air-gapped' actually mean here?",
    a: "Every model, vector index, and inference pipeline runs entirely inside your infrastructure — on-premise servers, private cloud (AWS/Azure/GCP with VPC + KMS), or a physical appliance we ship to your data center. No outbound calls to OpenAI, Anthropic, or Google. Zero external egress required for inference.",
  },
  {
    q: "How is this cheaper than OpenAI API?",
    a: "Per-token API costs scale linearly with usage — at any real volume ($15k+/mo of GPT-4 traffic), owning the infrastructure pays back in 4-9 months. After deployment, your marginal inference cost is essentially electricity + hardware amortization. Zero API bills.",
  },
  {
    q: "Which models do you deploy?",
    a: "We deploy the best open-weights model for the workload — Llama 3.3 70B for general reasoning, Qwen 3 for multilingual, Mistral Large for enterprise, LTX-2 / Wan 2.1 for video. All fine-tuned on your data, running under vLLM or TGI with Qdrant vector store, exposed via Open-WebUI or your own frontend.",
  },
  {
    q: "What's the difference between Sandbox, On-Prem, and Appliance?",
    a: "Sandbox is a 30-day POC on shared hardware — validate the workflow before you commit. On-Prem is a full deployment on your infrastructure (existing servers or new hardware we spec). Appliance is a physical rack unit we build, configure, and ship — plug it into your network and it's operational.",
  },
  {
    q: "Do you handle compliance mapping?",
    a: "Yes. Every deployment includes GDPR / HIPAA / SOC 2 alignment documented at hand-off. For regulated verticals (healthcare, finance, legal), we bundle our AI Risk & Compliance Audit ($3,500) at 50% off with any On-Prem or Appliance engagement.",
  },
];

export default function PrivateAILanding() {
  const tiers = tierSlugs.map((slug) => servicesContent[slug]).filter(Boolean);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="bg-slate-950 text-slate-100" data-testid="private-ai-landing">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-slate-800/80">
        <div className="absolute inset-0 te-grid-bg opacity-30" aria-hidden />
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.4, ease: "easeOut" }}
          className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-violet-600/20 blur-[140px]"
          aria-hidden
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.8, delay: 0.4 }}
          className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px]"
          aria-hidden
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16 lg:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-emerald-400/40 bg-emerald-500/[0.06] text-emerald-400 px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.22em]"
            data-testid="private-ai-hero-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 te-pulse-dot" />
            100% Air-Gapped · Zero Data Leakage
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 font-display text-4xl sm:text-6xl lg:text-8xl font-bold leading-[0.95] tracking-tighter max-w-5xl"
            data-testid="private-ai-hero-headline"
          >
            <span className="text-violet-400">Private AI</span>.{" "}
            <span className="text-emerald-400">Zero token fees</span>. <br className="hidden sm:block" />
            Your data. Your walls<span className="text-[#FF3355]">.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-8 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed"
          >
            We architect and deploy air-gapped private LLM infrastructure — Llama 3.3, Qwen 3,
            Mistral — running on your hardware with{" "}
            <span className="text-emerald-400 font-semibold">$0 recurring API costs</span> after
            deployment. Every prompt stays inside your walls.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            {trustBadges.map((b) => (
              <span
                key={b.label}
                className="inline-flex items-center gap-2 border border-emerald-400/25 bg-emerald-500/[0.04] text-emerald-400 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em]"
              >
                <b.Icon size={11} /> {b.label}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#tiers"
              data-testid="private-ai-hero-cta-primary"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold rounded-2xl hover:from-violet-500 hover:to-indigo-500 hover:scale-[1.02] transition-all shadow-[0_0_40px_-8px_rgba(139,92,246,0.6)]"
            >
              <Server size={14} /> Explore Deployment Tiers
            </a>
            <Link
              to="/services/audit-mid"
              data-testid="private-ai-hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 backdrop-blur-xl text-slate-200 px-7 py-4 rounded-2xl font-mono text-xs uppercase tracking-[0.22em] hover:bg-slate-800 hover:border-slate-600 hover:scale-[1.02] transition-all"
            >
              <ShieldCheck size={14} /> Book Compliance Audit
            </Link>
          </motion.div>

          {/* Model stack strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-14 border border-slate-800/80 bg-slate-900/60 backdrop-blur-xl rounded-2xl p-6"
            data-testid="private-ai-stack-strip"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-4">
              // deployment.stack — production-tested
            </div>
            <div className="flex flex-wrap gap-2">
              {stackTags.map((t) => {
                const colorClass =
                  t.accent === "violet"
                    ? "border-violet-500/40 text-violet-300 bg-violet-950/40"
                    : t.accent === "emerald"
                    ? "border-emerald-500/40 text-emerald-300 bg-emerald-950/40"
                    : "border-purple-500/40 text-purple-300 bg-purple-950/40";
                return (
                  <span
                    key={t.label}
                    className={`font-mono text-xs uppercase tracking-[0.15em] border px-3 py-1.5 rounded-full ${colorClass}`}
                  >
                    {t.label}
                  </span>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ COMPARISON ============ */}
      <section
        className="relative py-20 lg:py-28 border-b border-slate-800/80"
        data-testid="private-ai-comparison"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">
              // PUBLIC.CLOUD.TRAP · vs · TRACEELEMENTS.ENGINE
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">
              Public API risk vs.{" "}
              <span className="text-emerald-400">air-gapped sovereignty</span>.
            </h2>
          </motion.div>

          <div className="mt-12 grid lg:grid-cols-2 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="bg-slate-900/60 border border-red-900/40 backdrop-blur-xl rounded-2xl p-8"
              data-testid="private-ai-compare-trap"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-xl bg-red-950/50 border border-red-500/40 flex items-center justify-center">
                  <AlertTriangle size={16} className="text-red-400" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-red-400">
                    // A · PUBLIC CLOUD AI TRAP
                  </div>
                  <div className="font-display text-xl font-bold text-slate-100">
                    Leaky. Metered. Locked-in.
                  </div>
                </div>
              </div>
              <ul className="space-y-3">
                {comparison.map((c, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-slate-400 leading-relaxed"
                  >
                    <X size={14} className="text-red-400 mt-1 shrink-0" />
                    <span>{c.trap}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="bg-slate-900/60 border border-emerald-500/60 backdrop-blur-xl rounded-2xl p-8 shadow-[0_0_60px_-15px_rgba(52,211,153,0.35)]"
              data-testid="private-ai-compare-te"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-xl bg-emerald-950/50 border border-emerald-500/50 flex items-center justify-center">
                  <Cpu size={16} className="text-emerald-400" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">
                    // B · TRACEELEMENTS ENGINE
                  </div>
                  <div className="font-display text-xl font-bold text-emerald-400">
                    Sovereign. Owned. Instant.
                  </div>
                </div>
              </div>
              <ul className="space-y-3">
                {comparison.map((c, i) => (
                  <li
                    key={i}
                    className="te-lime-accent flex items-start gap-3 text-sm text-slate-100 leading-relaxed"
                  >
                    <Check size={14} className="text-emerald-400 mt-1 shrink-0" />
                    <span>{c.te}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ PRICING TIERS ============ */}
      <section
        id="tiers"
        className="relative py-20 lg:py-28 border-b border-slate-800/80 scroll-mt-24"
        data-testid="private-ai-tiers"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">
              // DEPLOYMENT.TIERS
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">
              From <span className="text-violet-400">POC</span> to{" "}
              <span className="text-emerald-400">rack unit</span>.
            </h2>
            <p className="mt-4 text-slate-400 max-w-2xl">
              Validate the workflow, deploy on your infrastructure, or take physical delivery of a
              plug-and-power rack appliance.
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {tiers.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className={`relative rounded-2xl p-8 flex flex-col backdrop-blur-xl ${
                  t.popular
                    ? "bg-slate-900/70 border-2 border-violet-500 shadow-[0_0_60px_-15px_rgba(139,92,246,0.6)] lg:-my-4"
                    : "bg-slate-900/60 border border-slate-800/80"
                }`}
                data-testid={`private-ai-tier-${tierSlugs[i]}`}
              >
                {t.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-gradient-to-r from-violet-500 to-indigo-500 text-white px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] font-bold shadow-[0_0_20px_-4px_rgba(139,92,246,0.8)]">
                    <Star size={10} fill="currentColor" /> Most Popular
                  </div>
                )}
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-violet-400">
                  {t.kicker}
                </div>
                <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-100">
                  {t.subtitle || t.name}
                </h3>
                <div className="mt-4">
                  <span className="font-display text-5xl font-bold text-slate-100">
                    {t.priceLabel}
                  </span>
                </div>
                <p className="mt-2 text-xs text-slate-400">{t.priceNote}</p>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {(t.deliverables || []).slice(0, 7).map((d, idx) => (
                    <li
                      key={idx}
                      className="te-lime-accent flex items-start gap-2 text-sm text-slate-300 leading-relaxed"
                    >
                      <Check size={13} className="text-emerald-400 mt-1 shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>

                {i === 0 ? (
                  <Link
                    to={`/portal?package=${tierSlugs[i]}`}
                    data-testid={`private-ai-tier-cta-${tierSlugs[i]}`}
                    className={`mt-8 w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] font-bold transition-all hover:scale-[1.02] ${
                      t.popular
                        ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-500 hover:to-indigo-500 shadow-[0_0_20px_-4px_rgba(139,92,246,0.6)]"
                        : "border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-400"
                    }`}
                  >
                    <Zap size={12} /> Start Instantly <ArrowUpRight size={12} />
                  </Link>
                ) : (
                  <Link
                    to={`/terminal?tier=${tierSlugs[i]}`}
                    data-testid={`private-ai-tier-cta-${tierSlugs[i]}`}
                    className={`mt-8 w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] font-bold transition-all hover:scale-[1.02] ${
                      t.popular
                        ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-500 hover:to-indigo-500 shadow-[0_0_20px_-4px_rgba(139,92,246,0.6)]"
                        : "border border-violet-500/60 text-violet-300 hover:bg-violet-500/10 hover:border-violet-400"
                    }`}
                  >
                    <ShieldCheck size={12} /> Book Strategy Session <ArrowUpRight size={12} />
                  </Link>
                )}

                <div className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  <Link
                    to={`/services/${tierSlugs[i]}`}
                    className="hover:text-violet-400 transition-colors"
                  >
                    full architecture spec →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ARCHITECTURE TERMINAL ============ */}
      <section
        className="relative py-20 lg:py-28 border-b border-slate-800/80 overflow-hidden"
        data-testid="private-ai-terminal"
      >
        <div className="absolute inset-0 pointer-events-none opacity-30">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full bg-violet-600/20 blur-[160px]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 border border-violet-500/40 bg-violet-950/30 text-violet-300 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] mb-6">
              <Terminal size={12} /> // ARCHITECTURE.PREVIEW
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter">
              A reference stack you actually <span className="text-emerald-400">own</span>.
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed">
              Every deployment is built on production-tested open-weights models paired with the
              observability, RAG, and inference layers your engineering team needs to operate
              confidently.
            </p>
            <div className="mt-8 space-y-3">
              {[
                { Icon: Server, label: "Inference · vLLM / TGI · GPU-optimized" },
                { Icon: Database, label: "Vector Store · Qdrant · self-hosted" },
                { Icon: Layers, label: "Orchestration · LangChain / LlamaIndex" },
                { Icon: Lock, label: "Access · Open-WebUI + SSO / SCIM" },
              ].map((r) => (
                <div key={r.label} className="flex items-center gap-3 font-mono text-sm text-slate-300">
                  <r.Icon size={14} className="text-violet-400 shrink-0" />
                  <span>{r.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="bg-slate-900/70 border border-violet-500/40 backdrop-blur-xl rounded-2xl overflow-hidden shadow-[0_0_80px_-20px_rgba(139,92,246,0.5)]"
          >
            <div className="border-b border-slate-800 px-5 py-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
              </div>
              <div className="ml-3 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                private-ai.local · deployment.log
              </div>
              <div className="ml-auto flex items-center gap-1.5 font-mono text-[9px] text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 te-pulse-dot" /> healthy
              </div>
            </div>
            <div className="p-5 font-mono text-xs leading-relaxed space-y-1">
              <div className="text-slate-500">$ trace-elements deploy --tier onprem --host local</div>
              <div className="text-emerald-400">✓ Model loaded · llama-3.3-70b-instruct · 4-bit quantized</div>
              <div className="text-emerald-400">✓ Vector store online · qdrant://localhost:6333 · 1.2M vectors</div>
              <div className="text-emerald-400">✓ Inference server · vLLM · 8× A100 · 224 tok/s</div>
              <div className="text-emerald-400">✓ RAG pipeline · LangChain · ingesting 12 sources</div>
              <div className="text-emerald-400">✓ Auth · SSO connected · SCIM provisioning live</div>
              <div className="text-emerald-400">✓ Observability · Langfuse · 100% trace capture</div>
              <div className="text-violet-300 mt-2">
                &gt; ready · zero outbound egress · $0 API costs
              </div>
              <div className="text-slate-500 mt-3 border-t border-slate-800 pt-3">
                deployment-time: 4h 12m · sovereignty: 100% · compliance: SOC 2 + HIPAA aligned
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section
        className="relative py-20 lg:py-28 border-b border-slate-800/80"
        data-testid="private-ai-faq"
      >
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="text-center"
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">
              // COMMON.QUESTIONS
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              Straight answers.
            </h2>
          </motion.div>

          <div className="mt-12 space-y-3">
            {faqs.map((f, i) => (
              <div
                key={i}
                className={`bg-slate-900/60 border rounded-2xl overflow-hidden backdrop-blur-xl transition-all ${
                  openFaq === i ? "border-violet-500/60" : "border-slate-800/80"
                }`}
                data-testid={`private-ai-faq-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-display text-base lg:text-lg font-semibold text-slate-100">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-violet-400 transition-transform shrink-0 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-slate-400 leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg opacity-30" aria-hidden />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-violet-600/15 blur-[140px]" />
        </div>

        <div className="relative max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-400 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] mb-8"
          >
            <ShieldCheck size={12} /> // TAKE.SOVEREIGNTY
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1 }}
            className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter text-slate-100"
          >
            Your data. <span className="text-emerald-400">Your walls.</span>{" "}
            <span className="text-violet-400">Zero</span> tokens.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-slate-400 text-base sm:text-lg max-w-2xl mx-auto"
          >
            30-day sandbox to validate. Full deployment in weeks. Own it forever.
          </motion.p>
          <motion.a
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.4 }}
            href="#tiers"
            data-testid="private-ai-bottom-cta"
            className="mt-12 inline-flex items-center gap-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-8 py-5 rounded-2xl font-mono text-sm uppercase tracking-[0.22em] font-bold hover:from-violet-500 hover:to-indigo-500 hover:scale-[1.03] transition-all shadow-[0_0_40px_-8px_rgba(139,92,246,0.6)]"
          >
            <Server size={18} /> Start with the Sandbox <ArrowUpRight size={18} />
          </motion.a>
        </div>
      </section>
    </div>
  );
}
